import { useEffect, useState, useRef } from "react";
import gsap from "gsap";
import { Icon } from "./Icons";
import { DarkToggle } from "./DarkToggle";
import { AnimatedBackground } from "./AnimatedBackground";
import { motion, AnimatePresence } from "framer-motion";

const fadeUpParams = {
  initial: { opacity: 0, y: 30 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: "-40px" },
  transition: { duration: 0.6, ease: [0.16, 1, 0.3, 1] }
};

const brands = [
  { name: "WOODPECKER", logo: "https://www.google.com/s2/favicons?domain=glwoodpecker.com&sz=128" },
  { name: "MANI", logo: "https://www.google.com/s2/favicons?domain=mani.co.jp&sz=128" },
  { name: "DENTSPLY", logo: "https://www.google.com/s2/favicons?domain=dentsplysirona.com&sz=128" },
  { name: "ORO", logo: "https://jaypeedent.com/wp-content/uploads/2025/04/Oro-Healthcare-Logo.png" },
  { name: "NSK", logo: "https://www.google.com/s2/favicons?domain=nsk-dental.com&sz=128" },
  { name: "3M", logo: "https://www.google.com/s2/favicons?domain=3m.com&sz=128" },
  { name: "ULTRADENT", logo: "https://www.google.com/s2/favicons?domain=ultradent.com&sz=128" },
  { name: "GDC", logo: "https://www.google.com/s2/favicons?domain=gdcdental.com&sz=128" },
  { name: "VOCO", logo: "https://cdn.dental-tribune.com/dti/0001/68b17323/cmVzaXplKHc9OTYwO2g9NDgwKTpzaGFycGVuKGxldmVsPTApOm91dHB1dChmb3JtYXQ9cG5nKQ/up/dt/2024/06/VOCO_logo_freigestellt-schmal-1.png" },
  { name: "PRIME", logo: "https://www.prime-dental.in/images/prime-dental.png" },
  { name: "IVOCLAR", logo: "https://download.logo.wine/logo/Ivoclar_Vivadent/Ivoclar_Vivadent-Logo.wine.png" },
  { name: "ZHERMACK", logo: "https://www.google.com/s2/favicons?domain=zhermack.com&sz=128" },
  { name: "PREVEST", logo: "https://www.prevestdenpro.com/wp-content/uploads/2021/06/cropped-Prevest-DenPro-LOGO-.png" },
];

function BrandMarquee() {
  const marqueeRef = useRef(null);

  useEffect(() => {
    const marquee = marqueeRef.current;
    if (!marquee) return;

    const totalWidth = marquee.scrollWidth / 4; // We duplicate items 3 times for a smooth loop

    const animation = gsap.to(marquee, {
      x: `-=${totalWidth}`,
      duration: 40, // Reduced speed (60% of 25s version) for better readability
      ease: "none",
      repeat: -1,
      onRepeat: () => {
        gsap.set(marquee, { x: 0 });
      }
    });

    return () => animation.kill();
  }, []);

  return (
    <div className="marquee-container">
      <div className="marquee-track" ref={marqueeRef}>
        {[...brands, ...brands, ...brands].map((brand, i) => (
          <div className="marquee-item" key={i}>
            <div className="marquee-logo">
              <img
                src={brand.logo}
                alt={brand.name}
                onLoad={(e) => {
                  e.target.style.opacity = 1;
                }}
                onError={(e) => {
                  e.target.style.display = 'none';
                }}
              />
            </div>
            <span className="marquee-name">{brand.name}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

export function Landing({ onNav, dark, setDark }) {
  const [navScrolled, setNavScrolled] = useState(false);

  useEffect(() => {
    const container = document.querySelector(".landing");
    if (!container) return;
    const handleScroll = () => setNavScrolled(container.scrollTop > 10);
    container.addEventListener("scroll", handleScroll, { passive: true });
    return () => container.removeEventListener("scroll", handleScroll);
  }, []);

  const features = [
    { icon: "equipment", title: "Premium Tools", desc: "Access a curated catalog of high-performance tools and equipment." },
    { icon: "truck",     title: "Fast Logistics", desc: "Enjoy 48-72 hour delivery with our streamlined supply chain." },
    { icon: "verified",  title: "Genuine Products", desc: "100% authentic items sourced directly from top global manufacturers." },
    { icon: "trending",  title: "Scale Your Practice", desc: "Volume discounts and steady supplies to keep your clinics running." },
    { icon: "headset",   title: "24/7 Support", desc: "Dedicated support team covering all technical and logistical needs." },
    { icon: "chart",     title: "Insight Dashboard", desc: "Track orders and inventory effortlessly in your secure user portal." }
  ];

  return (
    <div className="landing page-enter">
      {/* Navigation */}
      <nav className={`land-nav${navScrolled ? " land-nav--scrolled" : ""}`}>
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <div className="logo-mark"><img src="/logo.png" alt="K Dental" /></div>
          <span className="logo-text">K <span>Dental</span></span>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
          <DarkToggle dark={dark} setDark={setDark} />
          <button className="nav-login-btn" onClick={() => onNav("user-login")}>
            Login / Register <Icon name="arrowRight" size={12} />
          </button>
        </div>
      </nav>

      {/* Hero Section */}
      <div className="land-hero">
        <AnimatedBackground />

        <motion.div
          initial={{ opacity: 0, y: -20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, delay: 0.1 }}
          className="hero-eyebrow"
        >
          <Icon name="verified" size={13} />
          ISO 9001:2015 Certified Dental Logistics
        </motion.div>

        <motion.h1
          initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.2 }}
          className="hero-title"
        >
          Premium Dental Products &amp; <span>Equipments.</span>
        </motion.h1>

        <motion.p
          initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.3 }}
          className="hero-sub"
        >
          Empowering India's dental professionals with world-class equipment and seamless supply chain solutions.
        </motion.p>

        {/* Global Partners Marquee */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 1, delay: 0.6 }}
          className="land-partners-section"
        >
          <div className="partners-eyebrow">Authorized Distribution Network</div>

          <BrandMarquee />
        </motion.div>
      </div>

      {/* Stats */}
      <div className="land-stats">
        {[["500K+", "Happy Customers"], ["20,000+", "Products"], ["48-72Hrs", "Fast Delivery"], ["100%", "Genuine Products"]].map(([n, l]) => (
          <div className="lstat" key={l}>
            <span className="lstat-n">{n}</span>
            <span className="lstat-l">{l}</span>
          </div>
        ))}
      </div>

      {/* Features Grid Section */}
      <section className="land-features">
        <div className="features-header">
          <motion.span {...fadeUpParams} className="features-eyebrow">Key Use Cases</motion.span>
          <motion.h2 {...fadeUpParams} transition={{ delay: 0.1, duration: 0.6 }} className="features-title">
            Discover How K-Dental Equips Your Practice
          </motion.h2>
        </div>

        <div className="features-grid">
          {features.map((f, i) => (
            <motion.div
              key={i}
              className="f-card"
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-40px" }}
              transition={{ duration: 0.5, delay: i * 0.08, ease: "easeOut" }}
            >
              <div className="f-icon">
                <Icon name={f.icon} size={22} />
              </div>
              <h3>{f.title}</h3>
              <p>{f.desc}</p>
            </motion.div>
          ))}
        </div>
      </section>

      {/* Dark Banner Section */}
      <section className="land-dark-banner">
        <motion.div className="dark-banner-content" {...fadeUpParams}>
          <h2 className="dark-banner-title">Your Trusted Partner in Dental Supplies & Equipment</h2>
          <p className="dark-banner-sub">
           Trusted by Premier Dental Clinics across India, our intelligent platform simplifies supply chain management and ensures seamless access to premium dental products and equipment. K Dental is committed to delivering high-quality supplies to dentists and practitioners with efficiency and reliability.
          </p>
          <button className="dark-banner-btn" onClick={() => onNav("user-login")}>
            Open Store Now <Icon name="arrowRight" size={15} />
          </button>
        </motion.div>

        {/* Mockup visual representation */}
        <motion.div
          className="db-mockup"
          initial={{ opacity: 0, y: 80 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "0px" }}
          transition={{ duration: 0.8, delay: 0.2, ease: "easeOut" }}
        >
          <div className="db-mockup-app">
            <div className="db-m-sidebar">
              <div className="db-m-logo"><Icon name="verified" size={16} /></div>
              <div className="db-m-nav active"><div className="nav-pulse"></div></div>
              <div className="db-m-nav"></div>
              <div className="db-m-nav"></div>
              <div className="db-m-nav" style={{ marginTop: 'auto', opacity: 0.5 }}></div>
            </div>
            <div className="db-m-main">
              <div className="db-m-header">
                <div className="db-m-search"></div>
                <div style={{ display: 'flex', gap: 16, alignItems: 'center' }}>
                  <div className="db-m-btn-primary"></div>
                  <div className="db-m-profile"></div>
                </div>
              </div>
              <div className="db-m-stats">
                <div className="db-m-stat-card">
                  <div className="db-m-stat-title"></div>
                  <div className="db-m-stat-val"></div>
                  <div className="db-m-chart">
                    <div className="db-m-bar" style={{ height: '40%', animationDelay: '0.1s' }}></div>
                    <div className="db-m-bar" style={{ height: '70%', animationDelay: '0.2s', background: 'var(--blue)' }}></div>
                    <div className="db-m-bar" style={{ height: '50%', animationDelay: '0.3s' }}></div>
                    <div className="db-m-bar" style={{ height: '90%', animationDelay: '0.4s', background: 'var(--cyan)' }}></div>
                    <div className="db-m-bar" style={{ height: '60%', animationDelay: '0.5s' }}></div>
                  </div>
                </div>
                <div className="db-m-stat-card" style={{ flex: 0.6 }}>
                  <div className="db-m-stat-title" style={{ width: '60%' }}></div>
                  <div className="db-m-stat-val" style={{ width: '40%', background: 'var(--text2)' }}></div>
                  <div className="db-m-circle-chart">
                    <div className="db-m-circle-inner"></div>
                  </div>
                </div>
              </div>
              <div className="db-m-table">
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6 }}>
                  <div style={{ height: '12px', width: '20%', background: 'var(--text4)', borderRadius: 4 }}></div>
                  <div style={{ height: '12px', width: '8%', background: 'var(--bg3)', borderRadius: 4 }}></div>
                </div>
                <div className="db-m-row" style={{ animationDelay: '0.1s' }}>
                  <div className="db-m-product-img"><Icon name="box" size={22} /></div>
                  <div className="db-m-product-info">
                    <div className="db-m-product-name" style={{ width: '60%' }}></div>
                    <div className="db-m-product-cat" style={{ width: '25%' }}></div>
                  </div>
                  <div className="db-m-status active"></div>
                  <div className="db-m-price"></div>
                </div>
                <div className="db-m-row" style={{ animationDelay: '0.2s' }}>
                  <div className="db-m-product-img" style={{ background: 'var(--cyan-l)', color: 'var(--cyan)' }}><Icon name="chart" size={20} /></div>
                  <div className="db-m-product-info">
                    <div className="db-m-product-name" style={{ width: '80%' }}></div>
                    <div className="db-m-product-cat" style={{ width: '40%' }}></div>
                  </div>
                  <div className="db-m-status warning"></div>
                  <div className="db-m-price" style={{ width: '10%' }}></div>
                </div>
                <div className="db-m-row" style={{ animationDelay: '0.3s' }}>
                  <div className="db-m-product-img" style={{ background: 'var(--green-l)', color: 'var(--green)' }}><Icon name="buyer" size={22} /></div>
                  <div className="db-m-product-info">
                    <div className="db-m-product-name" style={{ width: '50%' }}></div>
                    <div className="db-m-product-cat" style={{ width: '20%' }}></div>
                  </div>
                  <div className="db-m-status active"></div>
                  <div className="db-m-price" style={{ width: '18%' }}></div>
                </div>
              </div>
            </div>
          </div>
        </motion.div>
      </section>

    </div>
  );
}
